import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, MapPin } from "lucide-react";
import { MaskLines, Reveal } from "@/components/site/Reveal";

const HUB = "Kansas City";
const POINTS = ["Technicians dispatched to any US location", "Pay only for the visits you need", "Photo-verified job close-out"];

const startX = (d) => parseFloat(d.slice(1));

const arc = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const my = Math.min(a.y, b.y) - Math.abs(a.x - b.x) * 0.18 - 20;
  return `M${a.x},${a.y} Q${mx},${my} ${b.x},${b.y}`;
};

const UsMap = ({ data, active, setActive }) => {
  const hub = data.cities.find((c) => c.name === HUB);
  const routes = data.cities.filter((c) => c.name !== HUB && c.name !== "Honolulu" && c.name !== "Anchorage");
  return (
    <motion.svg viewBox="0 0 975 610" className="h-auto w-full" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }} role="img" aria-label="Map of the United States with all 50 states covered">
      <defs>
        <linearGradient id="state-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#1777AF" />
          <stop offset="1" stopColor="#29C1D3" />
        </linearGradient>
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
      </defs>
      <g>
        {data.states.map((s) => (
          <motion.path
            key={s.id}
            d={s.d}
            data-testid={`map-state-${s.id}`}
            onMouseEnter={() => setActive(s.name)}
            onClick={() => setActive(s.name)}
            variants={{ hidden: { opacity: 0.35 }, show: { opacity: 1, transition: { duration: 0.6, delay: (startX(s.d) / 975) * 1.4 } } }}
            className={`cursor-pointer stroke-[#0E2657] transition-[fill] duration-300 ${active === s.name ? "fill-white" : "fill-[url(#state-grad)] hover:fill-[#7FE3EE]"}`}
            strokeWidth="1"
          />
        ))}
      </g>
      <g fill="none" stroke="#ffffff" strokeOpacity="0.55" strokeWidth="1.2" className="map-routes">
        {hub && routes.map((c) => <path key={c.name} d={arc(hub, c)} />)}
      </g>
      {data.cities.map((c, i) => (
        <g key={c.name} transform={`translate(${c.x},${c.y})`}>
          <circle r="10" fill="#ffffff" opacity="0.5" className="map-ping" style={{ animationDelay: `${(i % 6) * 0.4}s` }} />
          <circle r={c.name === HUB ? 6 : 3.5} fill={c.name === HUB ? "#ffffff" : "#0E2657"} stroke="#ffffff" strokeWidth="1.5" />
        </g>
      ))}
    </motion.svg>
  );
};

export const CoverageMap = () => {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch("/us-states.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive && Array.isArray(j?.states)) setData(j); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <section data-testid="coverage-map-section" className="relative overflow-hidden bg-[#0E2657] py-24 sm:py-32 grain">
      <div className="grid-lines absolute inset-0 opacity-50" />
      <div className="absolute right-0 top-1/4 h-[520px] w-[720px] rounded-full bg-[#29C1D3]/20 blur-[140px]" />
      <div className="container-x relative grid items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <p className="eyebrow text-cyan-300">Nationwide onsite support</p>
          <MaskLines lines={["All 50 states.", { text: "One phone call.", className: "text-grad" }]} className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight text-white sm:text-5xl" />
          <Reveal delay={0.15}>
            <p className="mt-6 leading-relaxed text-slate-300">
              Wherever your offices, stores or job sites are, we send a technician — and you only pay for the visits you need.
            </p>
            <ul className="mt-8 space-y-3">
              {POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-200">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" /> {p}
                </li>
              ))}
            </ul>
            <Link to="/contact?need=onsite-support" data-testid="coverage-cta" className="btn-primary mt-10">
              Request onsite support <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <div onMouseLeave={() => setActive(null)} className="relative">
            {data && <UsMap data={data} active={active} setActive={setActive} />}
            <div data-testid="coverage-state-label" className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white backdrop-blur">
              <MapPin className="h-4 w-4 text-cyan-300" />
              {active ? <span><strong>{active}</strong> — covered</span> : <span>Hover or tap any state — every one is covered</span>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
